import { useQuery }   from 'react-query';
import { HttpClient } from 'apiclient/ApiClient';

// Grade & program catalog, served by GET /programs. fcs/programs.py is the single
// source of truth — grade ids, display labels, and the grade→program mapping are
// never hard-coded client-side (review #340 item 9).

export interface IProgramOption {
    id: string;      // game program id, e.g. "5_6_basic"
    label: string;
}

export interface IGradeOption {
    id: string;      // stable grade id persisted in Class.number, e.g. "kindergarten"
    label: string;
    program: string; // the grade's default game program
}

export interface IProgramCatalog {
    programs: IProgramOption[];
    grades: IGradeOption[];
}

const EMPTY_CATALOG: IProgramCatalog = { programs: [], grades: [] };

export function useProgramCatalog(): IProgramCatalog {
    const client = HttpClient();
    const { data } = useQuery(
        '/programs',
        async () => await client.get<IProgramCatalog>('/programs'),
        { staleTime: Infinity, cacheTime: Infinity },
    );
    return data ?? EMPTY_CATALOG;
}

// Program id → display label ('' while the catalog loads / for unknown ids, e.g. "common").
export function programLabel(catalog: IProgramCatalog, program?: string | null): string {
    if (!program) return '';
    return catalog.programs.find((p) => p.id === program)?.label ?? '';
}

// Class.number → display label. Pre-grade-id classes store the label itself (or free
// text like "Pre-K") — render those verbatim.
export function gradeLabel(catalog: IProgramCatalog, grade?: string | null): string {
    if (!grade) return '';
    return catalog.grades.find((g) => g.id === grade)?.label ?? grade;
}

// Class.number → the grade's default game program (undefined for legacy free-text
// grades, mirroring fcs/programs.py program_for_grade).
export function gradeProgram(catalog: IProgramCatalog, grade?: string | null): string | undefined {
    if (!grade) return undefined;
    return catalog.grades.find((g) => g.id === grade || g.label === grade)?.program;
}
